"use client";

import { SearchX, ArrowLeft, Home, Sparkles } from "lucide-react";
import Link from "next/link";
import { Typography } from "@/components/common/ui/Typography";

export interface NotFoundProps {
  title?: string;
  description?: string;
  showBackButton?: boolean;
}

export function NotFound({
  title = "404 - Page Not Found",
  description = "The page you're looking for doesn't exist",
  showBackButton = true,
}: NotFoundProps) {
  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center p-6">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-purple-300/30 rounded-full blur-3xl animate-blob" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-pink-300/30 rounded-full blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl animate-blob animation-delay-4000" />
      </div>

      <div className="max-w-3xl w-full relative z-10">
        <div className="text-center mb-12">
          <div className="relative inline-block mb-8">
            <div className="absolute inset-0 bg-navigator-primary/30 rounded-full blur-2xl animate-pulse" />
            <div className="relative bg-white rounded-full p-8 shadow-2xl border-4 border-purple-200">
              <SearchX
                className="w-20 h-20 text-navigator-primary"
                strokeWidth={1.5}
              />
            </div>
            <div className="absolute -top-2 -right-2">
              <Sparkles className="w-8 h-8 text-purple-400 animate-pulse" />
            </div>
          </div>

          <Typography variant="display1" color="text-navigator-primary-dark" className="md:text-6xl mb-4 tracking-tight">
            {title}
          </Typography>

          <Typography variant="headline3" weight="normal" color="muted" as="p" className="mb-3">
            {description}
          </Typography>

          <Typography variant="body" color="light" as="p" className="max-w-xl mx-auto leading-relaxed">
            The page you're trying to reach might have been moved, deleted, or
            never existed. Let's get you back on track.
          </Typography>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
          <Link href="/home" className="w-full sm:w-auto">
            <div className="group relative overflow-hidden bg-navigator-primary hover:bg-navigator-primary-dark text-white rounded-2xl transition-all duration-300 shadow-lg hover:shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
              <div className="relative flex items-center justify-center gap-3 px-8 py-4">
                <Home className="w-5 h-5" />
                <span className="font-semibold text-lg">Go to Dashboard</span>
              </div>
            </div>
          </Link>

          {showBackButton && (
            <button
              onClick={() => window.history.back()}
              className="w-full sm:w-auto group"
            >
              <div className="bg-white hover:bg-gray-50 text-navigator-primary rounded-2xl transition-all duration-300 shadow-md hover:shadow-xl border-2 border-navigator-primary/20 hover:border-navigator-primary/40">
                <div className="flex items-center justify-center gap-3 px-8 py-4">
                  <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                  <span className="font-semibold text-lg">Go Back</span>
                </div>
              </div>
            </button>
          )}
        </div>

        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/80 backdrop-blur-sm rounded-full shadow-md border border-gray-200">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <Typography variant="body" color="muted" as="span">
              Need help? Contact our support team
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
}
